/* eslint-disable indent */
import React from 'react';
import {
  GridColDef,
  GridRenderCellParams,
  GridRowClassNameParams,
} from '@mui/x-data-grid';
import i18n from '../../../translations/i18n';
import { PlannedActivity } from '../../../api/activitiesApi/interfaces';
import { AgentBadge } from '../../../components/AgentBadge';
import dayjs from 'dayjs';
import { formatLocaleDate } from '../../../helpers/dateHelpers';
import { ValueCell, ValueListCell } from '../../../components/data-table';
import WarningIcon from '@mui/icons-material/Warning';
import { Typography } from '@mui/material';
import { capitalizeStr } from '../../../helpers/stringHelpers';

export const getColumns = (hideClientColumn: boolean = false): GridColDef[] => [
  {
    field: 'performAt',
    headerName: i18n.t('entities:plannedActivity.performAt'),
    width: 180,
    renderCell: (params) => {
      const performAt = dayjs(params.row.performAt);
      const overdue = performAt.diff(dayjs(), 'day') < 0;
      const dateAsStr = formatLocaleDate(params.row.performAt, false);

      if (overdue) {
        return (
          <ValueCell
            sx={{
              gap: '5px',
            }}
          >
            <WarningIcon
              color="error"
              sx={{
                width: '20px',
                height: '20px',
              }}
            />
            <Typography variant="body2">{dateAsStr}</Typography>
          </ValueCell>
        );
      } else {
        return dateAsStr;
      }
    },
    hideable: false,
  },
  {
    field: 'startTime',
    headerName: capitalizeStr(i18n.t('words.time')),
    width: 100,
    renderCell: (params) => {
      const startTime = params.row.startTime;
      const endTime = params.row.endTime;
      const timeStr = endTime ? `${startTime} - ${endTime}` : startTime;

      return <ValueCell value={timeStr} />;
    },
    hideable: true,
    sortable: false,
  },
  {
    field: 'title',
    headerName: i18n.t('entities:plannedActivity.title'),
    minWidth: 150,
    flex: 1,
    valueGetter: (title: PlannedActivity['title']) => title,
  },
  {
    field: 'action',
    headerName: i18n.t('entities:activityLog.action'),
    width: 150,
    valueGetter: (action: PlannedActivity['action']) => action.name,
  },
  {
    field: 'contacts',
    headerName: i18n.t('entities:activityLog.contacts'),
    width: 200,
    renderCell: (params: GridRenderCellParams<PlannedActivity>) => {
      const contacts = params.row.contacts;

      return (
        <ValueListCell
          values={contacts?.map(
            (contact) => `${contact.firstName} ${contact.lastName}`
          )}
        />
      );
    },
  },
  ...(!hideClientColumn
    ? [
        {
          field: 'client',
          headerName: i18n.t('entities:activityLog.client'),
          width: 250,
          valueGetter: (client: PlannedActivity['client']) => client.name,
        },
      ]
    : []),
  {
    field: 'group',
    headerName: i18n.t('entities:activityLog.group'),
    width: 150,
    renderCell: (params) => {
      const client = params.row.client as PlannedActivity['client'];

      return client.group?.name || '-';
    },
  },
  {
    field: 'agent',
    headerName: i18n.t('entities:activityLog.agent'),
    width: 200,
    renderCell: (params) => {
      const row = params.row;

      return <AgentBadge agent={row.agent} />;
    },
  },
];

export const getRowClassName = (
  params: GridRowClassNameParams<PlannedActivity>
): string => {
  const performAt = dayjs(params.row.performAt);
  const daysLeft = performAt.diff(dayjs(), 'day');

  if (daysLeft < 0) {
    return 'row-overdue';
  } else if (daysLeft <= 7) {
    return 'row-danger';
  } else if (daysLeft <= 30) {
    return 'row-warning';
  }

  return '';
};
